import React from "react";
import { Link } from "react-router-dom";
import {
  FaLock,
  FaUser,
  FaUserPlus,
  FaUserCheck,
  FaSearch,
  FaClock,
} from "react-icons/fa";
import ProfileBanner from "../../components/profile/ProfileBanner";
import getImageUrl from "../../utils/imageUtils";
import Loader from "../../components/common/Loader";
import styles from "../../styles/pages/user/UserProfilePage.module.css";

const PrivateProfileView = ({
  user,
  stats,
  isFollowing,
  hasPendingRequest,
  currentUser,
  isOwnProfile,
  followLoading,
  onFollow,
  onFollowersClick,
  onFollowingClick,
  showFollowers,
  showFollowing,
  followersList,
  followingList,
  loadingFollowers,
  loadingFollowing,
  onCloseFollowers,
  onCloseFollowing,
}) => {
  const [followersSearch, setFollowersSearch] = React.useState("");
  const [followingSearch, setFollowingSearch] = React.useState("");

  const filteredFollowers = followersList.filter((follower) => {
    if (!followersSearch.trim()) return true;
    const searchLower = followersSearch.toLowerCase();
    const fullName = `${follower.firstName} ${follower.lastName}`.toLowerCase();
    const username = follower.username?.toLowerCase() || "";
    return (
      fullName.includes(searchLower) ||
      username.includes(searchLower) ||
      follower.firstName?.toLowerCase().includes(searchLower) ||
      follower.lastName?.toLowerCase().includes(searchLower)
    );
  });

  const filteredFollowing = followingList.filter((following) => {
    if (!followingSearch.trim()) return true;
    const searchLower = followingSearch.toLowerCase();
    const fullName =
      `${following.firstName} ${following.lastName}`.toLowerCase();
    const username = following.username?.toLowerCase() || "";
    return (
      fullName.includes(searchLower) ||
      username.includes(searchLower) ||
      following.firstName?.toLowerCase().includes(searchLower) ||
      following.lastName?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className={styles.privateProfileContainer}>
      <ProfileBanner user={user} isOwnProfile={isOwnProfile} />
      <div className={styles.privateProfileHeader}>
        <div className={styles.privateProfileInfo}>
          <div className={styles.privateAvatarSection}>
            {user.avatar ? (
              <img
                src={getImageUrl(user.avatar)}
                alt={`${user.firstName} ${user.lastName}`}
                className={styles.privateAvatar}
              />
            ) : (
              <div className={styles.privateAvatarPlaceholder}>
                <FaUser size={60} />
              </div>
            )}
          </div>
          <div className={styles.privateProfileDetails}>
            <h1 className={styles.privateName}>
              {user.firstName} {user.lastName}
            </h1>
            {user.username && (
              <p className={styles.privateUsername}>@{user.username}</p>
            )}
            <div className={styles.privateMessage}>
              <FaLock className={styles.privateIcon} />
              <p>This account is private</p>
              <span className={styles.privateSubtext}>
                Follow this account to see their posts and content
              </span>
            </div>
          </div>
        </div>
        {currentUser && (
          <div className={styles.privateActions}>
            <button
              className={`${styles.privateFollowButton} ${
                isFollowing ? styles.following : ""
              } ${hasPendingRequest ? styles.pendingRequest : ""}`}
              onClick={onFollow}
              disabled={followLoading}
            >
              {isFollowing ? (
                <>
                  <FaUserCheck /> Following
                </>
              ) : hasPendingRequest ? (
                <>
                  <FaClock /> Request Sent
                </>
              ) : (
                <>
                  <FaUserPlus /> Follow
                </>
              )}
            </button>
          </div>
        )}
      </div>
      <div className={styles.privateStatsBar}>
        <div className={styles.privateStatItem} onClick={onFollowersClick}>
          <span className={styles.privateStatNumber}>
            {stats?.followers || 0}
          </span>
          <span className={styles.privateStatText}>Followers</span>
        </div>
        <div className={styles.privateStatItem} onClick={onFollowingClick}>
          <span className={styles.privateStatNumber}>
            {stats?.following || 0}
          </span>
          <span className={styles.privateStatText}>Following</span>
        </div>
      </div>
      <div className={styles.privateContentPlaceholder}>
        <FaLock size={64} className={styles.privateContentIcon} />
        <h2>This account is private</h2>
        <p>Follow this account to see their posts and content</p>
      </div>

      {showFollowers && (
        <div className={styles.modalOverlay} onClick={onCloseFollowers}>
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2>Followers</h2>
              <button className={styles.modalClose} onClick={onCloseFollowers}>
                ×
              </button>
            </div>
            <div className={styles.modalSearchContainer}>
              <div className={styles.searchInputWrapper}>
                <FaSearch className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search followers..."
                  value={followersSearch}
                  onChange={(e) => setFollowersSearch(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
            </div>
            <div className={styles.modalBody}>
              {loadingFollowers ? (
                <Loader inline={true} />
              ) : filteredFollowers.length > 0 ? (
                <div className={styles.usersGrid}>
                  {filteredFollowers.map((follower) => (
                    <Link
                      key={follower._id}
                      to={`/user/${follower._id}`}
                      className={styles.userCard}
                      onClick={onCloseFollowers}
                    >
                      {follower.avatar ? (
                        <img
                          src={getImageUrl(follower.avatar)}
                          alt={follower.firstName}
                          className={styles.userAvatar}
                        />
                      ) : (
                        <div className={styles.userAvatarPlaceholder}>
                          <FaUser />
                        </div>
                      )}
                      <div className={styles.userCardInfo}>
                        <div className={styles.userCardName}>
                          {follower.firstName} {follower.lastName}
                        </div>
                        {follower.username && (
                          <div className={styles.userCardUsername}>
                            @{follower.username}
                          </div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              ) : followersList.length > 0 ? (
                <div className={styles.emptyState}>
                  <p>No followers match your search</p>
                </div>
              ) : (
                <div className={styles.emptyState}>
                  <p>No followers yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showFollowing && (
        <div className={styles.modalOverlay} onClick={onCloseFollowing}>
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2>Following</h2>
              <button className={styles.modalClose} onClick={onCloseFollowing}>
                ×
              </button>
            </div>
            <div className={styles.modalSearchContainer}>
              <div className={styles.searchInputWrapper}>
                <FaSearch className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search following..."
                  value={followingSearch}
                  onChange={(e) => setFollowingSearch(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
            </div>
            <div className={styles.modalBody}>
              {loadingFollowing ? (
                <Loader inline={true} />
              ) : filteredFollowing.length > 0 ? (
                <div className={styles.usersGrid}>
                  {filteredFollowing.map((following) => (
                    <Link
                      key={following._id}
                      to={`/user/${following._id}`}
                      className={styles.userCard}
                      onClick={onCloseFollowing}
                    >
                      {following.avatar ? (
                        <img
                          src={getImageUrl(following.avatar)}
                          alt={following.firstName}
                          className={styles.userAvatar}
                        />
                      ) : (
                        <div className={styles.userAvatarPlaceholder}>
                          <FaUser />
                        </div>
                      )}
                      <div className={styles.userCardInfo}>
                        <div className={styles.userCardName}>
                          {following.firstName} {following.lastName}
                        </div>
                        {following.username && (
                          <div className={styles.userCardUsername}>
                            @{following.username}
                          </div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              ) : followingList.length > 0 ? (
                <div className={styles.emptyState}>
                  <p>No users match your search</p>
                </div>
              ) : (
                <div className={styles.emptyState}>
                  <p>Not following anyone yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivateProfileView;
